import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import P2Blurbs from "components/UI/P2Blurbs/P2Blurbs";
import React, { useState } from "react";
import { observer } from "mobx-react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import theme from "Theme/AppTheme";
import { Blurb } from "components/UI/AcxTranscription/Components/BlurbBox/Blurb";

interface EddySectionProps {
    store: ConversationsStore;
    setTab: React.Dispatch<React.SetStateAction<number>>;
}

const EddySection: React.FC<EddySectionProps> = observer(
    ({ store, setTab }) => {
        const canCollapse =
            store.selectedConversation?.hasEddyEffect.containsExamples;
        const [open, setOpen] = useState(true);

        return (
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(2),
                }}
            >
                <Box sx={{ width: "85%" }}>
                    {canCollapse && (
                        <Tooltip
                            title={`${
                                open ? "Collapse" : "Expand"
                            } Eddy Effect Highlights`}
                        >
                            <IconButton
                                onClick={() => {
                                    setOpen(!open);
                                }}
                                size="small"
                                edge="start"
                                sx={{
                                    padding: 0,
                                    marginRight: theme.spacing(0.25),
                                }}
                                id={"expand-eddy-highlights"}
                            >
                                {open ? (
                                    <KeyboardArrowUpIcon />
                                ) : (
                                    <KeyboardArrowDownIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}

                    <Typography
                        id="conversation-summary-eddy-effect"
                        component="span"
                        onClick={() => {
                            setOpen(!open);
                        }}
                        sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginBottom: theme.spacing(1),
                            ...(canCollapse && {
                                "&:hover": {
                                    cursor: "pointer",
                                },
                            }),
                            scrollMarginTop: "20px",
                        }}
                    >
                        {!store.selectedConversation?.hasEddyEffect
                            .containsExamples
                            ? "Eddy Signals: "
                            : "Eddy Signals"}
                    </Typography>
                    {!store.selectedConversation?.hasEddyEffect
                        .containsExamples && (
                        <Typography component="span">
                            No specific examples identified in transcript
                        </Typography>
                    )}
                    <Typography sx={{ fontWeight: 400, fontSize: "13px" }}>
                        {store.selectedConversation?.hasEddyEffect
                            .containsExamples && (
                            <>
                                <Typography
                                    component="span"
                                    fontWeight={700}
                                    color={theme.palette.highlight.eddy.main}
                                >
                                    Eddy Signals{" "}
                                </Typography>
                                found in this conversation
                            </>
                        )}
                    </Typography>
                </Box>
                <Collapse in={open}>
                    {store.selectedConversation?.isChat ? (
                        <P2Blurbs
                            data={store.selectedConversation?.eddyData ?? []}
                            chat={store.selectedConversation.chatTranscription}
                            speakerChannelMap={
                                store.speakerStore.speakerChannelMap
                            }
                            variant="eddy"
                        />
                    ) : (
                        <P2Blurbs
                            data={store.selectedConversation?.eddyData ?? []}
                            transcription={
                                store.selectedConversation?.audioTranscription
                                    ?.phrases ??
                                store.selectedConversation?.audioTranscription
                                    ?.words ??
                                []
                            }
                            speakerChannelMap={
                                store.speakerStore.speakerChannelMap
                            }
                            isPhrasesTranscript={
                                !!store.selectedConversation?.audioTranscription
                                    ?.phrases
                            }
                            variant="eddy"
                            onBlurbClick={(blurb) => {
                                if (blurb && "timeInterval" in blurb) {
                                    // Handle transcript blurb click
                                    const transcriptBlurb = blurb as Blurb;
                                    if (store.transcriptionStore) {
                                        const index =
                                            store.transcriptionStore.getScrollIndexForTimestamp(
                                                transcriptBlurb.timeInterval[0],
                                            );
                                        store.transcriptionStore.setScrollIndex(
                                            index,
                                        );
                                        setTab(1);
                                    }
                                }
                            }}
                        />
                    )}
                </Collapse>
            </Box>
        );
    },
);

export default EddySection;
